import {
    PATIENT_TAG_GROUPS_FRAGMENT,
    groupTagNamesByGroup,
} from '@/components/tags/patientTagGroups';
import { useRedirectIfNotFound } from '@/hooks/useRedirectIfNotFound';
import { FeatureFlag } from '@/types/featureFlag';
import { OrderType } from '@/types/graphql';
import { renderLanguage } from '@/types/language';
import { IOrderFrequency, IOrderPrn } from '@/types/order';
import { renderSex } from '@/types/sex';
import {
    AddressInTwoLines,
    BASIC_ADDRESS_WITH_COORDINATES_FRAGMENT,
    basicAddressToAddress,
} from '@/utils/addresses';
import { formatPhoneNumberForDisplay } from '@/utils/formatting';
import { useConfigValue } from '@/utils/useConfigValue';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import {
    Box,
    Button,
    ButtonProps,
    Collapse,
    Divider,
    IconButton,
    Link,
    Skeleton,
    Typography,
    TypographyProps,
} from '@mui/material';
import moment from 'moment';
import { type ReactNode, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { copyToClipboard } from '../../buttons/CopyButton';
import { DocumentViewerAndDropzone } from '../../displayFields/documents';
import { ExternalIdDisplay } from '../../ids/ExternalIdDisplay';
import { CaptionLabel } from '../../labels/CaptionLabel';
import { NoneLabel, NoneLabelProps } from '../../labels/NoneLabel';
import { NotesEditor } from '../../notes/NotesEditor';
import { HorizontalStack, HorizontalStackProps, VerticalStack } from '../../utils/linearLayouts';
import { PatientStatusTag, PatientStatusTagFragment } from '../PatientStatusTag';
import { PatientCareTeamInfo } from './PatientCareTeamInfo';
import { CareTeamClinicianFragment } from './PatientCareTeamInfo.fragments';
import { PatientLeftRailQuery, PatientLeftRailQueryVariables } from './PatientLeftRail.gql.types';
import { PatientLeftRailAvailabilitySection } from './PatientLeftRailAvailabilitySection';
import { PatientLeftRailEpisodeSection } from './PatientLeftRailEpisodeSection';
import { PatientLeftRailEpisode } from './PatientLeftRailEpisodeSection.fragments';
import { PatientLeftRailVisitsSection } from './PatientLeftRailVisitsSection';

export const PATIENT_LEFT_RAIL_QUERY = gql`
    query PatientLeftRail(
        $patientId: UUID!
        $episodesEnabled: Boolean! = true
        $today: Date = null
    ) {
        organizations {
            totalCount
        }
        patient(id: $patientId) {
            id
            organization {
                id
                name
                integrations
            }
            createdAt
            fullName
            firstName
            lastName
            phoneNumber
            email
            dateOfBirth
            externalId
            externalLink
            externalIds {
                ...ExternalId
            }
            sex
            preferredClinicianSex
            linkToPatientVisits(includeQueryParams: false)
            notes
            notesForClinician
            preferredLanguage
            timezone
            defaultEpisode @include(if: $episodesEnabled) {
                ...PatientLeftRailEpisode
            }
            lastVisit: visits(
                filters: {
                    state: { inList: [ENDED, IN_REVIEW] }
                    effectiveDate: { lte: $today }
                    outcome: { inList: [NONE, COMPLETED] }
                }
                ordering: { effectiveDate: DESC }
                pagination: { limit: 1, offset: 0 }
            ) @skip(if: $episodesEnabled) {
                items {
                    id
                    effectiveDate
                    clinicianAppointments {
                        id
                        clinician {
                            id
                            fullName
                        }
                        services {
                            id
                            name
                        }
                    }
                }
            }
            nextVisit: visits(
                filters: {
                    state: { inList: [UNSCHEDULED, SCHEDULED, CONFIRMED] }
                    effectiveDate: { gte: $today }
                }
                ordering: { effectiveDate: ASC }
                pagination: { limit: 1, offset: 0 }
            ) @skip(if: $episodesEnabled) {
                items {
                    id
                    effectiveDate
                    clinicianAppointments {
                        id
                        clinician {
                            id
                            fullName
                        }
                        services {
                            id
                            name
                        }
                    }
                }
            }
            careTeams {
                id
                clinicians {
                    ...CareTeamClinicianFragment
                }
            }
            payers {
                id
                name
                description
            }
            preferredTimeslots {
                day
                startTime
                endTime
            }
            addresses {
                ...BasicAddressWithCoordinates
            }
            permissions {
                id
                callPatient
                editDetails
                editCareTeam
                editAvailability
                viewCommunications
                sendCommunications
                viewDocuments
                editDocuments
                viewInternalNotes
                editInternalNotes
                viewNotesForClinician
                editNotesForClinician
            }
            documents {
                ...DocumentListItemFragment
            }
            ...PatientTagGroups
            ...PatientStatusTag
        }
    }
    ${BASIC_ADDRESS_WITH_COORDINATES_FRAGMENT}
    ${PATIENT_TAG_GROUPS_FRAGMENT}
    ${CareTeamClinicianFragment}
    ${PatientLeftRailEpisode}
    ${PatientStatusTagFragment}
`;

type IOrderDiscipline = {
    id: string;
    code: string;
};
export interface IOrder {
    id: string;
    orderType: OrderType;
    episode: {
        id: string;
    } | null;
    discipline: IOrderDiscipline | null;
    frequency: IOrderFrequency | null;
    prn: IOrderPrn | null;
}

type PatientInfo = NonNullable<PatientLeftRailQuery['patient']>;

const LabelTypography = ({ children }: { children: ReactNode }) => (
    <Typography
        sx={{ minWidth: '60px' }}
        variant='body2'
        fontSize={'12px'}
        lineHeight={'16px'}
        color={'#1E1E1E'}
    >
        {children}
    </Typography>
);

export const LeftRailTextButton = ({ sx, ...rest }: ButtonProps) => {
    return (
        <Button
            {...rest}
            size='small'
            variant='text'
            sx={{ color: '#08786C', textTransform: 'none', ...sx }}
        />
    );
};

export const LeftRailNoneLabel = ({ sx, ...rest }: NoneLabelProps) => {
    return <NoneLabel sx={{ fontSize: '14px', lineHeight: '20px', ...sx }} {...rest} />;
};

export const LeftRailDiscontinuedLabel = ({ sx, ...rest }: NoneLabelProps) => {
    return <LeftRailNoneLabel sx={sx} {...rest} />;
};

export const HeadingCaptionLabel = ({ children, ...rest }: TypographyProps) => (
    <CaptionLabel
        fontSize={'12px'}
        lineHeight={'16px'}
        color={'#1E1E1E'}
        fontWeight='500'
        {...rest}
    >
        {children}
    </CaptionLabel>
);

export const valueTypographyProps = {
    color: '#757575',
    fontSize: '14px',
    lineHeight: '20px',
};

export const LeftRailValueTypography = ({
    children,
    ...props
}: { children: ReactNode } & TypographyProps) => (
    <Typography variant='body2' component='div' {...valueTypographyProps} {...props}>
        {children}
    </Typography>
);

export const PatientInfoFieldDisplay = ({
    label,
    value,
    noneLabelText,
    stackProps,
}: {
    label: string;
    value: ReactNode;
    noneLabelText?: string;
    stackProps?: Omit<HorizontalStackProps, 'children'>;
}) => {
    const isStringValue = typeof value === 'string';

    return (
        <HorizontalStack spacing={4} alignItems='flex-start' fullWidth {...stackProps}>
            <Box sx={{ py: 0.5 }}>
                <LabelTypography>{label}</LabelTypography>
            </Box>
            {value ? (
                <HorizontalStack spacing={1} alignItems='center'>
                    <LeftRailValueTypography>{value}</LeftRailValueTypography>
                    {isStringValue && (
                        <IconButton
                            size='small'
                            onClick={() => void copyToClipboard(value)}
                            sx={{ padding: '4px' }}
                            aria-label='Copy to clipboard'
                        >
                            <ContentCopy sx={{ fontSize: '16px', color: '#757575' }} />
                        </IconButton>
                    )}
                </HorizontalStack>
            ) : (
                <LeftRailNoneLabel text={noneLabelText} />
            )}
        </HorizontalStack>
    );
};

const EDIT_PATIENT_NOTES_MUTATION = gql`
    mutation PatientLeftRailEditPatientNotes($patientId: UUID!, $notes: String!) {
        editPatientNotes(input: { patientId: $patientId, notes: $notes }) {
            id
            notes
        }
    }
`;

const EDIT_PATIENT_NOTES_FOR_CLINICIAN_MUTATION = gql`
    mutation PatientLeftRailEditPatientNotesForClinician($patientId: UUID!, $notes: String!) {
        editPatientNotesForClinician(input: { patientId: $patientId, notesForClinician: $notes }) {
            id
            notesForClinician
        }
    }
`;

export const PatientOfficeNotesEditor = ({
    patient,
    iconActions,
}: {
    patient: PatientInfo;
    iconActions?: boolean;
}) => {
    const { id, notes, permissions } = patient;
    const [editPatientNotes] = useMutation(EDIT_PATIENT_NOTES_MUTATION);

    const handleSave = async (notesValue: string) => {
        await editPatientNotes({
            variables: {
                patientId: id,
                notes: notesValue,
            },
        });
    };

    return (
        <NotesEditor
            notes={notes}
            canView={permissions.viewInternalNotes}
            canEdit={permissions.editInternalNotes}
            onSave={handleSave}
            notesInfoIconProps={{
                visibleToClinicians: false,
                headline: 'Notes about the patient',
                description:
                    'These notes will not be visible to clinicians; they will only be shown in the dashboard.',
            }}
            captionLabel={
                <HeadingCaptionLabel>
                    <span style={{ whiteSpace: 'nowrap' }}>Patient Notes</span>{' '}
                    <span style={{ whiteSpace: 'nowrap' }}>(Office Only)</span>
                </HeadingCaptionLabel>
            }
            iconActions={iconActions}
        />
    );
};

export const PatientNotes = ({ patient }: { patient: PatientInfo }) => {
    const { id, permissions } = patient;
    const [editPatientNotesForClinician] = useMutation(EDIT_PATIENT_NOTES_FOR_CLINICIAN_MUTATION);

    const handleSaveNotesForClinician = async (notesValue: string) => {
        await editPatientNotesForClinician({
            variables: {
                patientId: id,
                notes: notesValue,
            },
        });
    };

    return (
        <>
            <PatientOfficeNotesEditor patient={patient} />
            <NotesEditor
                notes={patient.notesForClinician}
                canView={permissions.viewNotesForClinician}
                canEdit={permissions.editNotesForClinician}
                onSave={handleSaveNotesForClinician}
                notesInfoIconProps={{
                    visibleToClinicians: true,
                    headline: 'Notes about the patient for clinicians',
                    description:
                        'These notes will be visible to clinicians who are assigned to the visit.',
                }}
                captionLabel={
                    <HeadingCaptionLabel>Patient Notes for Clinicians</HeadingCaptionLabel>
                }
            />
        </>
    );
};

const PatientLeftRailContent = ({
    patient,
    episodesEnabled,
    ordersEnabled,
}: {
    patient: PatientInfo;
    episodesEnabled: boolean;
    ordersEnabled: boolean;
}) => {
    const {
        externalId,
        externalIds,
        externalLink,
        fullName,
        phoneNumber,
        email,
        dateOfBirth,
        sex,
        addresses,
        preferredLanguage,
        payers,
        tags,
    } = patient;
    const isDemoOrg = useConfigValue('is_demo_org');
    const [isPatientDetailsExpanded, setIsPatientDetailsExpanded] = useState(false);

    const age = moment().diff(dateOfBirth, 'years');
    const dateOfBirthString = moment(dateOfBirth).format('MM/DD/YYYY');

    const tagsByGroup = useMemo(() => groupTagNamesByGroup(tags?.items ?? []), [tags]);

    return (
        <VerticalStack>
            <VerticalStack
                position='sticky'
                top={0}
                bgcolor='#2E5D5B'
                p={3}
                color='#f3f3f3'
                gap={1}
                zIndex={1}
            >
                <HorizontalStack justifyContent='space-between' alignItems='flex-start'>
                    <VerticalStack>
                        <Link
                            component={RouterLink}
                            to={`/patients/${patient.id}`}
                            sx={{
                                textDecoration: 'none',
                                color: '#FFF',
                                fontSize: '22px',
                                lineHeight: '28px',
                            }}
                        >
                            {fullName}
                        </Link>
                        <Typography fontSize='14px' lineHeight='20px' fontWeight='500'>
                            {renderSex(sex)}, {age} years, {dateOfBirthString}
                        </Typography>
                    </VerticalStack>
                    <PatientStatusTag
                        isDeceased={patient.isDeceased}
                        deceasedAt={patient.deceasedAt}
                        isActive={patient.isActive}
                        sx={{
                            backgroundColor: '#1C3937',
                            color: '#FFFFFF',
                        }}
                    />
                </HorizontalStack>
            </VerticalStack>
            <VerticalStack p={2} spacing={2}>
                <VerticalStack spacing={2}>
                    <HorizontalStack justifyContent='space-between'>
                        <Typography
                            color='#08786C'
                            variant='h2'
                            fontSize='16px'
                            lineHeight='24px'
                            fontWeight='500'
                        >
                            Patient Details
                        </Typography>
                        <IconButton
                            onClick={() => setIsPatientDetailsExpanded(!isPatientDetailsExpanded)}
                            aria-label={
                                isPatientDetailsExpanded
                                    ? 'Collapse patient details'
                                    : 'Expand patient details'
                            }
                            aria-expanded={isPatientDetailsExpanded}
                        >
                            {isPatientDetailsExpanded ? (
                                <ExpandLess sx={{ color: '#14AE5C' }} />
                            ) : (
                                <ExpandMore sx={{ color: '#14AE5C' }} />
                            )}
                        </IconButton>
                    </HorizontalStack>
                    <VerticalStack spacing={2}>
                        <HorizontalStack spacing={4} alignItems='flex-start' fullWidth>
                            <LabelTypography>
                                {addresses.length > 1 ? 'Locations' : 'Location'}
                            </LabelTypography>
                            <Box display='flex' flexDirection='column' gap={1}>
                                {patient.addresses.length > 0 ? (
                                    patient.addresses.map((address) => (
                                        <LeftRailValueTypography key={address.id}>
                                            <AddressInTwoLines
                                                address={basicAddressToAddress(address)}
                                                shouldWrap={true}
                                            />
                                        </LeftRailValueTypography>
                                    ))
                                ) : (
                                    <LeftRailNoneLabel />
                                )}
                            </Box>
                        </HorizontalStack>
                        <PatientInfoFieldDisplay
                            label='Phone'
                            value={formatPhoneNumberForDisplay(phoneNumber ?? '')}
                        />
                        <PatientInfoFieldDisplay
                            label='External ID'
                            stackProps={{ alignItems: 'center' }}
                            value={
                                <ExternalIdDisplay
                                    label={false}
                                    externalId={externalId}
                                    externalLink={externalLink}
                                    externalIds={externalIds}
                                />
                            }
                        />
                        <Collapse in={isPatientDetailsExpanded}>
                            <VerticalStack spacing={2}>
                                <PatientInfoFieldDisplay label='Email' value={email} />
                                <PatientInfoFieldDisplay
                                    label='Language'
                                    value={renderLanguage(preferredLanguage)}
                                />
                                <PatientInfoFieldDisplay
                                    label='Payers'
                                    value={
                                        payers.length
                                            ? payers
                                                  .map((payer) => payer.name)
                                                  .sort((a, b) => a.localeCompare(b))
                                                  .join(', ')
                                            : 'None'
                                    }
                                />
                            </VerticalStack>
                        </Collapse>
                    </VerticalStack>
                </VerticalStack>
                {tagsByGroup.size > 0 &&
                    Array.from(tagsByGroup.values()).map((group) => (
                        <VerticalStack key={group.id} spacing={0.5}>
                            <HeadingCaptionLabel>{group.name}</HeadingCaptionLabel>
                            <LeftRailValueTypography>
                                {group.tagNames.join(', ')}
                            </LeftRailValueTypography>
                        </VerticalStack>
                    ))}
                <Divider orientation='horizontal' flexItem />
                <VerticalStack spacing={2}>
                    {episodesEnabled && patient.defaultEpisode && (
                        <PatientLeftRailEpisodeSection
                            episode={patient.defaultEpisode}
                            ordersEnabled={ordersEnabled}
                        />
                    )}

                    {!episodesEnabled && (
                        <PatientLeftRailVisitsSection
                            lastVisit={patient.lastVisit?.items[0]}
                            nextVisit={patient.nextVisit?.items[0]}
                        />
                    )}

                    <PatientCareTeamInfo patient={patient} />
                    {isDemoOrg && <PatientLeftRailAvailabilitySection patient={patient} />}

                    <DocumentViewerAndDropzone
                        documents={patient.documents}
                        canView={patient.permissions.viewDocuments}
                        canEdit={patient.permissions.editDocuments}
                        objectType='patient'
                        objectId={patient.id}
                        captionLabel='Documents'
                    />
                    <PatientNotes patient={patient} />
                </VerticalStack>
            </VerticalStack>
        </VerticalStack>
    );
};

const LEFT_RAIL_POLL_INTERVAL_MS = 30_000;

export const PatientLeftRail = ({ patientId }: { patientId: string }) => {
    const episodesEnabled = useFeatureFlagEnabled(FeatureFlag.EPISODES_OF_CARE);
    const ordersEnabled = useFeatureFlagEnabled(FeatureFlag.ORDERS);

    const { data, loading } = useQuery<PatientLeftRailQuery, PatientLeftRailQueryVariables>(
        PATIENT_LEFT_RAIL_QUERY,
        {
            variables: { patientId, episodesEnabled, today: moment().format('YYYY-MM-DD') },
            errorPolicy: 'all',
            pollInterval: LEFT_RAIL_POLL_INTERVAL_MS,
        },
    );

    useRedirectIfNotFound(
        !loading && !data?.patient,
        '/patients',
        "The patient you're looking for could not be found.",
    );

    if (loading && !data?.patient) {
        return <Skeleton variant='rectangular' height='100%' />;
    }

    if (!data?.patient) {
        return null;
    }

    return (
        <PatientLeftRailContent
            patient={data.patient}
            episodesEnabled={episodesEnabled}
            ordersEnabled={ordersEnabled}
        />
    );
};
